import "./data-protection.css";
import { Container, Divider, Grid, Header, List } from "semantic-ui-react";

const DataProtection = () => {
    return (
        <Container className="data-protection">
            <Grid doubling columns={2}>
                <Grid.Column width={6}>
                    <Header
                        as="h3"
                        content="Data Protection"
                        className="data-protection-h3"
                    />
                </Grid.Column>
                <Grid.Column width={10}>
                    <p className="data-protection-description">
                        This privacy notice explains how VIS Ventilation Industrial Systems Ltd uses personal data
                        through visgroup.co.uk, including the contact form, direct contact channels, and Google
                        reCAPTCHA.
                    </p>

                    <div className="data-protection-section">
                        <Header
                            as="h4"
                            content="Who We Are"
                            className="data-protection-h4"
                        />
                        <p className="data-protection-text">
                            VIS Ventilation Industrial Systems Ltd is responsible for deciding why and how the personal
                            data described in this notice is used in connection with visgroup.co.uk and website
                            enquiries.
                        </p>
                        <List relaxed className="data-protection-list">
                            <List.Item>
                                <strong>Address:</strong> 41 Rothersthorpe Avenue, Northampton NN4 8JH, United Kingdom
                            </List.Item>
                            <List.Item>
                                <strong>Telephone:</strong>{" "}
                                <a href="tel:+441604217969">+44 1604 217969</a>
                            </List.Item>
                            <List.Item>
                                <strong>Email:</strong>{" "}
                                <a href="mailto:info@visgroup.co.uk">info@visgroup.co.uk</a>
                            </List.Item>
                            <List.Item>
                                <strong>Website:</strong>{" "}
                                <a href="https://visgroup.co.uk" target="_blank" rel="noreferrer">
                                    visgroup.co.uk
                                </a>
                            </List.Item>
                        </List>
                    </div>

                    <Divider />

                    <div className="data-protection-section">
                        <Header
                            as="h4"
                            content="Personal Data We Collect"
                            className="data-protection-h4"
                        />
                        <List bulleted className="data-protection-list">
                            <List.Item>
                                Contact form details: first name, last name, email address, subject, message, and the
                                reCAPTCHA token submitted with the form.
                            </List.Item>
                            <List.Item>
                                Contact channel details: any information you include when you contact us by email,
                                telephone, or post.
                            </List.Item>
                            <List.Item>
                                Security and technical data connected to spam prevention and form delivery, including
                                information processed by Google reCAPTCHA and the AWS submission flow.
                            </List.Item>
                        </List>
                    </div>

                    <Divider />

                    <div className="data-protection-section">
                        <Header
                            as="h4"
                            content="How We Use Your Data And Our Lawful Bases"
                            className="data-protection-h4"
                        />
                        <List relaxed className="data-protection-list">
                            <List.Item>
                                <strong>Responding to enquiries and requests:</strong> legitimate interests in running
                                the business and communicating with customers, suppliers, applicants, and other
                                enquirers.
                            </List.Item>
                            <List.Item>
                                <strong>Taking steps before entering a contract:</strong> where your enquiry is about
                                products, services, pricing, project delivery, or recruitment and you ask us to take
                                action before any agreement is made.
                            </List.Item>
                            <List.Item>
                                <strong>Keeping records of business communications:</strong> legitimate interests in
                                administering enquiries, follow-up, and internal record keeping.
                            </List.Item>
                            <List.Item>
                                <strong>Protecting the website and contact form from spam or abuse:</strong> legitimate
                                interests in site security and service availability.
                            </List.Item>
                        </List>
                    </div>

                    <Divider />

                    <div className="data-protection-section">
                        <Header
                            as="h4"
                            content="Contact Form, AWS, And Google reCAPTCHA"
                            className="data-protection-h4"
                        />
                        <p className="data-protection-text">
                            When you submit the website contact form, the form data is sent to an AWS API Gateway
                            endpoint.
                            Google reCAPTCHA is used to help distinguish genuine human submissions from automated abuse.
                        </p>
                        <p className="data-protection-text">
                            No non-essential analytics or marketing cookies are identified in this repository. The
                            Google service used here is reCAPTCHA, which may set the necessary <strong>_GRECAPTCHA</strong>{" "}
                            cookie and process technical information required for risk analysis and fraud prevention.
                        </p>
                    </div>

                    <Divider />

                    <div className="data-protection-section">
                        <Header
                            as="h4"
                            content="Recipients And Processors"
                            className="data-protection-h4"
                        />
                        <List bulleted className="data-protection-list">
                            <List.Item>Relevant staff handling the enquiry or follow-up.</List.Item>
                            <List.Item>Amazon Web Services, which provides the contact form submission infrastructure.</List.Item>
                            <List.Item>Google, which provides the reCAPTCHA anti-abuse service.</List.Item>
                            <List.Item>
                                Email, telephony, and IT service providers used to receive, relay, or manage business
                                communications.
                            </List.Item>
                        </List>
                    </div>

                    <Divider />

                    <div className="data-protection-section">
                        <Header
                            as="h4"
                            content="International Transfers"
                            className="data-protection-h4"
                        />
                        <p className="data-protection-text">
                            The contact form submission endpoint referenced in this repository uses AWS in the{" "}
                            <strong>eu-west-2</strong> region. Google reCAPTCHA may process technical data on systems
                            outside the UK. If a restricted transfer occurs, it should be covered by an appropriate
                            safeguard provided by the relevant service provider.
                        </p>
                    </div>

                    <Divider />

                    <div className="data-protection-section">
                        <Header
                            as="h4"
                            content="Retention"
                            className="data-protection-h4"
                        />
                        <p className="data-protection-text">
                            We expect enquiry data to be kept only for as long as needed to review, respond to, and
                            manage the enquiry and any related follow-up, after which it should be deleted or archived
                            only where genuinely necessary.
                        </p>
                    </div>

                    <Divider />

                    <div className="data-protection-section">
                        <Header
                            as="h4"
                            content="Your Rights"
                            className="data-protection-h4"
                        />
                        <List bulleted className="data-protection-list">
                            <List.Item>The right to be informed about how your data is used.</List.Item>
                            <List.Item>The right of access to personal data we hold about you.</List.Item>
                            <List.Item>The right to rectification of inaccurate or incomplete data.</List.Item>
                            <List.Item>The right to erasure in certain circumstances.</List.Item>
                            <List.Item>The right to restriction of processing in certain circumstances.</List.Item>
                            <List.Item>The right to object to processing based on legitimate interests.</List.Item>
                            <List.Item>The right to data portability where the law applies.</List.Item>
                            <List.Item>
                                The right to withdraw consent where processing relies on consent, although ordinary
                                contact enquiries are not generally handled on that basis.
                            </List.Item>
                        </List>
                        <p className="data-protection-text">
                            To exercise these rights, contact{" "}
                            <a href="mailto:info@visgroup.co.uk">info@visgroup.co.uk</a>.
                        </p>
                    </div>

                    <Divider />

                    <div className="data-protection-section">
                        <Header
                            as="h4"
                            content="Complaints"
                            className="data-protection-h4"
                        />
                        <p className="data-protection-text">
                            Please contact us first if you have a concern about how your personal data has been
                            handled. You also have the right to complain to the Information Commissioner&apos;s Office
                            (ICO). The ICO complaint route is available at{" "}
                            <a
                                href="https://ico.org.uk/make-a-complaint/data-protection-complaints/"
                                target="_blank"
                                rel="noreferrer"
                            >
                                ico.org.uk/make-a-complaint/data-protection-complaints/
                            </a>.
                        </p>
                    </div>
                </Grid.Column>
            </Grid>
        </Container>
    );
};

export default DataProtection;
